import {
  AgentRole,
  Architecture,
  byteLength,
  MAX_FIREWALLS_PER_SERVER,
  MAX_NETWORKS_PER_SERVER,
  MAX_SERVERS_PER_CREATE,
  MAX_USER_DATA_BYTES,
  MAX_VOLUMES_PER_SERVER,
  SERVER_NAME_PATTERN,
  serverNames,
  ServerTypeFamily,
  type CreateServerRequest,
  type Image,
  type Network,
  type PriceList,
  type Region,
  type ServerType,
} from "@cloudserver/contracts";
import { z } from "zod";

/*
 * The create-server form, kept in the URL so a configuration can be shared or
 * reloaded. Everything here is pure: the page reads the search params, fills
 * in defaults from the catalogue and asks these functions what is valid and
 * what it costs. Cloud-init user data is not in the URL: it can be long and
 * often carries secrets, which do not belong in browser history.
 */

export const IMAGE_TABS = ["os", "apps", "own"] as const;
export const TYPE_SORTS = ["price", "cores", "memory", "disk"] as const;
export type TypeSort = (typeof TYPE_SORTS)[number];

const opt = <T extends z.ZodType>(schema: T) => schema.optional().catch(undefined);

export const CreateSearch = z.object({
  region: opt(z.string()),
  image: opt(z.string()),
  /** Our id of one of the project's snapshots or backups; wins over `image`. */
  snapshot: opt(z.string()),
  imageTab: opt(z.enum(IMAGE_TABS)),
  type: opt(z.string()),
  family: opt(ServerTypeFamily),
  arch: opt(Architecture),
  sort: opt(z.enum(TYPE_SORTS)),
  ipv4: opt(z.boolean()),
  ipv6: opt(z.boolean()),
  keys: opt(z.array(z.string()).max(50)),
  /** Our ids of private networks and firewalls, like the keys. */
  networks: opt(z.array(z.string()).max(MAX_NETWORKS_PER_SERVER)),
  firewalls: opt(z.array(z.string()).max(MAX_FIREWALLS_PER_SERVER)),
  /** Our ids of free volumes of the region to attach (one server only), and mounting them. */
  volumes: opt(z.array(z.string()).max(MAX_VOLUMES_PER_SERVER)),
  automount: opt(z.boolean()),
  /** Our id of a placement group to create the servers in. */
  placement: opt(z.string()),
  backups: opt(z.boolean()),
  /** Autopilot on (docs/autopilot.md), and whether it watches Docker containers (default on). */
  autopilot: opt(z.boolean()),
  containers: opt(z.boolean()),
  /** The Autopilot agent role (docs/autopilot.md "Agent roles"); monitoring only when absent. */
  role: opt(AgentRole),
  /** The browser terminal's CA at first boot; on unless turned off (`false`). */
  terminal: opt(z.boolean()),
  /** Rows of the labels editor, also unfinished ones. */
  labels: opt(z.array(z.tuple([z.string(), z.string()])).max(64)),
  name: opt(z.string().max(253)),
  count: opt(z.number().int().min(1).max(MAX_SERVERS_PER_CREATE)),
  /** Opened from the project dashboard: go back there after create. */
  from: opt(z.literal("dashboard")),
  /** Where the server was dropped on the dashboard (canvas units); it is placed there. */
  at: opt(z.tuple([z.number().int(), z.number().int()])),
});
export type CreateSearch = z.infer<typeof CreateSearch>;

export interface Catalogue {
  regions: Region[];
  types: ServerType[];
  images: Image[];
  prices: PriceList;
}

/** Whether a type can be ordered in a region: listed as available and priced there. */
export function offeredIn(type: ServerType, region: string): boolean {
  return Boolean(
    type.regions.find((r) => r.region === region)?.available &&
    type.prices.some((p) => p.region === region),
  );
}

export function priceIn(type: ServerType, region: string) {
  return type.prices.find((p) => p.region === region) ?? null;
}

const monthly = (type: ServerType, region: string) =>
  Number(priceIn(type, region)?.monthly.net ?? Number.POSITIVE_INFINITY);

/**
 * The cheapest type offered in the region, of the architecture asked for (x86
 * by default), with at least `minDiskGb` of disk (a snapshot needs its own).
 */
export function defaultType(
  types: ServerType[],
  region: string,
  arch: Architecture = "x86",
  minDiskGb = 0,
): ServerType | undefined {
  return types
    .filter((t) => t.architecture === arch && offeredIn(t, region) && t.diskGb >= minDiskGb)
    .sort((a, b) => monthly(a, region) - monthly(b, region) || a.code.localeCompare(b.code))[0];
}

const versionNumber = (v: string | null) =>
  (v ?? "").split(/[.-]/).map((part) => Number(part) || 0);

/** Newer versions first: 26.04 before 24.04, 10 before 9. */
export function byVersionDesc(a: Image, b: Image): number {
  const x = versionNumber(a.osVersion);
  const y = versionNumber(b.osVersion);
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const diff = (y[i] ?? 0) - (x[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/** System images of one architecture, grouped by OS, newest version first. */
export function osGroups(
  images: Image[],
  arch: Architecture,
): { flavor: string; versions: Image[] }[] {
  const groups = new Map<string, Image[]>();
  for (const image of images) {
    if (image.kind !== "system" || image.architecture !== arch || !image.code) continue;
    groups.set(image.osFlavor, [...(groups.get(image.osFlavor) ?? []), image]);
  }
  return [...groups.entries()].map(([flavor, versions]) => ({
    flavor,
    versions: versions.sort(byVersionDesc),
  }));
}

/** "Ubuntu 26.04" → "Ubuntu": the OS name as the provider writes it. */
export function osName(image: Image): string {
  const name = image.osVersion
    ? image.description.replace(image.osVersion, "").trim()
    : image.description;
  return name || image.osFlavor;
}

/** Newest Ubuntu when there is one, else the newest image of the first OS. */
export function defaultImage(images: Image[], arch: Architecture): Image | undefined {
  const groups = osGroups(images, arch);
  return (groups.find((g) => g.flavor === "ubuntu") ?? groups[0])?.versions[0];
}

/** `<os>-<type>-<region>`, plus the first free `-<n>` when one server is created. */
export function defaultName(
  osFlavor: string,
  type: string,
  region: string,
  count: number,
  taken: ReadonlySet<string>,
): string {
  const base = `${osFlavor}-${type}-${region}`.toLowerCase().replace(/[^a-z0-9-]/g, "-");
  if (count > 1) return base;
  for (let n = 1; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

const LABEL_PART = /^([a-z0-9A-Z]([a-z0-9A-Z._-]{0,61}[a-z0-9A-Z])?)?$/;

export type LabelProblem = "keyMissing" | "keyInvalid" | "valueInvalid" | "duplicate";

/** What is wrong with each labels row, or null; rows with neither key nor value are ignored. */
export function labelProblems(rows: [string, string][]): (LabelProblem | null)[] {
  const seen = new Set<string>();
  return rows.map(([key, value]) => {
    const k = key.trim();
    const v = value.trim();
    if (!k && !v) return null;
    if (!k) return "keyMissing";
    if (k.length > 63 || !LABEL_PART.test(k)) return "keyInvalid";
    if (v.length > 63 || !LABEL_PART.test(v)) return "valueInvalid";
    if (seen.has(k)) return "duplicate";
    seen.add(k);
    return null;
  });
}

export type NameProblem = "invalid" | "tooLong" | "taken";

export function nameProblem(
  name: string,
  count: number,
  taken: ReadonlySet<string>,
): NameProblem | null {
  if (!SERVER_NAME_PATTERN.test(name)) return "invalid";
  const names = serverNames(name, count);
  if (names.some((n) => n.length > 253)) return "tooLong";
  if (names.some((n) => taken.has(n))) return "taken";
  return null;
}

/** Whether a server in this network zone can join the network: it needs a cloud subnet there. */
export const networkFits = (network: Network, zone: string): boolean =>
  network.subnets.some((s) => s.type === "cloud" && s.zone === zone);

/**
 * How the address choice stands: no address at all is an error unless a
 * private network is chosen; then, like IPv6 only, it is a warning, because
 * the server is reachable only from inside the network.
 */
export function addressState(
  ipv4: boolean,
  ipv6: boolean,
  networks: number,
): "ok" | "warn" | "error" {
  if (!ipv4 && !ipv6) return networks > 0 ? "warn" : "error";
  return ipv4 ? "ok" : "warn";
}

export const userDataTooLarge = (text: string) => byteLength(text) > MAX_USER_DATA_BYTES;

/** Price of one server per hour and month (net), split into its parts. */
export interface PriceBreakdown {
  server: { hourly: number; monthly: number };
  ipv4: { hourly: number; monthly: number } | null;
  backups: { hourly: number; monthly: number } | null;
  /** One server, all parts. */
  each: { hourly: number; monthly: number };
  /** Times the number of servers. */
  total: { hourly: number; monthly: number };
}

export function priceBreakdown(input: {
  type: ServerType | undefined;
  region: string;
  prices: PriceList | undefined;
  ipv4: boolean;
  backups: boolean;
  count: number;
}): PriceBreakdown | null {
  const price = input.type ? priceIn(input.type, input.region) : null;
  if (!price || !input.prices) return null;
  const server = { hourly: Number(price.hourly.net), monthly: Number(price.monthly.net) };
  const ip = input.prices.publicIpv4.find((p) => p.region === input.region);
  const ipv4 =
    input.ipv4 && ip ? { hourly: Number(ip.hourly.net), monthly: Number(ip.monthly.net) } : null;
  const share = Number(input.prices.backupPercent) / 100;
  const backups = input.backups
    ? { hourly: server.hourly * share, monthly: server.monthly * share }
    : null;
  const parts = [server, ipv4, backups].filter((p) => p !== null);
  const each = {
    hourly: parts.reduce((sum, p) => sum + p.hourly, 0),
    monthly: parts.reduce((sum, p) => sum + p.monthly, 0),
  };
  return {
    server,
    ipv4,
    backups,
    each,
    total: { hourly: each.hourly * input.count, monthly: each.monthly * input.count },
  };
}

/** The request for the API from the completed form (call only when the form is valid). */
export function toRequest(input: {
  name: string;
  count: number;
  region: string;
  type: string;
  /** A catalogue code, or our id of a snapshot or backup. */
  image: { code: string } | { imageId: string };
  keys: string[];
  networks: string[];
  firewalls: string[];
  ipv4: boolean;
  ipv6: boolean;
  backups: boolean;
  /** Trust the organisation's SSH CA from the first boot (browser terminal). */
  terminal?: boolean;
  labels: [string, string][];
  userData: string;
  /** Free volumes of the region to attach, and whether to mount them. */
  volumes?: string[];
  automount?: boolean;
  /** Our id of a placement group, or null. */
  placement?: string | null;
  /** Autopilot with its container choice and agent role, or null when off. */
  autopilot?: { containers: boolean; role: AgentRole } | null;
}): CreateServerRequest {
  const labels = Object.fromEntries(
    input.labels.map(([k, v]) => [k.trim(), v.trim()] as const).filter(([k]) => k !== ""),
  );
  return {
    name: input.name.trim(),
    count: input.count,
    region: input.region,
    serverType: input.type,
    ...("code" in input.image ? { image: input.image.code } : { imageId: input.image.imageId }),
    sshKeyIds: input.keys,
    networkIds: input.networks,
    firewallIds: input.firewalls,
    publicIpv4: input.ipv4,
    publicIpv6: input.ipv6,
    backups: input.backups,
    browserTerminal: input.terminal ?? true,
    labels,
    ...(input.userData.trim() ? { userData: input.userData } : {}),
    ...(input.volumes && input.volumes.length > 0
      ? { volumeIds: input.volumes, automount: input.automount ?? false }
      : {}),
    ...(input.placement ? { placementGroupId: input.placement } : {}),
    ...(input.autopilot ? { autopilot: input.autopilot } : {}),
  };
}
